import { ChapterCounts, ChapterStats, answeredCount, correctRatio } from './weakness';
import { formatCorrectPercent } from './correct-percent';

/** The overview has exactly two tabs. */
export type PaperKey = 'A' | 'B';

/**
 * Paper A (basic sciences) is defined by the Capital ids of the practice bank:
 * 5 Neurophysiology, 6 General Physiology, 7 Cardiovascular Pharmacology, 8 CNS Pharmacology,
 * 9 General Pharmacology, 10 General Physics, 11 Statistics, 12 Clinical Measurement and
 * 19 Cardiorespiratory Physiology. Paper B is everything else (ids 13-18: anaesthesia,
 * intensive care, internal medicine, emergency medicine), so no chapter can ever disappear.
 * Grouping happens in the client only; the API order (order_num) is not touched.
 */
export const PAPER_A_CAPITAL_IDS: ReadonlyArray<number> = [5, 6, 7, 8, 9, 10, 11, 12, 19];
export const PAPER_B_CAPITAL_IDS: ReadonlyArray<number> = [13, 14, 15, 16, 17, 18];

/** A chapter needs at least this many answered questions before it gets a traffic light. */
export const MIN_ANSWERED_FOR_RATING = 5;
/** Below this share correct the light is red. */
export const RED_BELOW_PERCENT = 60;
/** Up to and including this share correct the light is yellow, above it is green. */
export const YELLOW_UP_TO_PERCENT = 75;

export type TrafficLight = 'none' | 'red' | 'yellow' | 'green';

export interface ChapterRow extends ChapterStats {
  total_question: number;
  blank_question_count: number | null | undefined;
}

export function paperOf(capitalId: number): PaperKey {
  return PAPER_A_CAPITAL_IDS.indexOf(capitalId) !== -1 ? 'A' : 'B';
}

/** Splits the chapters by paper and keeps the incoming (API) order inside each paper. */
export function splitByPaper<T extends { id: number }>(rows: T[]): { A: T[]; B: T[] } {
  return {
    A: rows.filter(row => paperOf(row.id) === 'A'),
    B: rows.filter(row => paperOf(row.id) === 'B')
  };
}

/**
 * Displayed chapter numbers: consecutive in the default order after grouping, Paper A first
 * (Paper A = 1..9, Paper B = 10..15). Keyed by Capital id, so the number stays fixed when the list is re-sorted.
 */
export function chapterNumbersFor<T extends { id: number }>(paperA: T[], paperB: T[]): Map<number, number> {
  const numbers = new Map<number, number>();
  paperA.concat(paperB).forEach((row, index) => numbers.set(row.id, index + 1));
  return numbers;
}

/**
 * Traffic light by share correct among answered questions (right / (right + wrong)):
 * red below 60 %, yellow 60-75 %, green above 75 %, none with fewer than 5 answered questions.
 * Uses integer maths, so exactly 60 % and exactly 75 % are yellow.
 */
export function trafficLight(row: ChapterCounts): TrafficLight {
  const answered = answeredCount(row);
  if (answered < MIN_ANSWERED_FOR_RATING) {
    return 'none';
  }
  const rightTimes100 = Math.max(0, Number(row.correct_question_count) || 0) * 100;
  if (rightTimes100 < RED_BELOW_PERCENT * answered) {
    return 'red';
  }
  if (rightTimes100 <= YELLOW_UP_TO_PERCENT * answered) {
    return 'yellow';
  }
  return 'green';
}

export interface BarSegments {
  correct: number;
  wrong: number;
  open: number;
}

/** Bar widths in percent of all questions of the chapter or paper; they add up to 100 (or all 0). */
export function barSegments(correct: number, wrong: number, total: number): BarSegments {
  const right = Math.max(0, Number(correct) || 0);
  const incorrect = Math.max(0, Number(wrong) || 0);
  const all = Math.max(Number(total) || 0, right + incorrect);
  if (all === 0) {
    return { correct: 0, wrong: 0, open: 0 };
  }
  const correctPart = (right / all) * 100;
  const wrongPart = (incorrect / all) * 100;
  return { correct: correctPart, wrong: wrongPart, open: Math.max(0, 100 - correctPart - wrongPart) };
}

export interface PaperSummary {
  total: number;
  correct: number;
  wrong: number;
  blank: number;
  answered: number;
  percent: string;
}

export function summarize(rows: ChapterRow[]): PaperSummary {
  const sum = (pick: (row: ChapterRow) => number | null | undefined) =>
    rows.reduce((acc, row) => acc + (Math.max(0, Number(pick(row)) || 0)), 0);
  const correct = sum(r => r.correct_question_count);
  const wrong = sum(r => r.wrong_question_count);
  return {
    total: sum(r => r.total_question),
    correct,
    wrong,
    blank: sum(r => r.blank_question_count),
    answered: correct + wrong,
    percent: formatCorrectPercent(correct, wrong)
  };
}

/**
 * Stable ascending sort by share correct. Chapters that have a rating (>= 5 answered)
 * come first; chapters with too little data keep their original order at the end,
 * so a single wrong answer never pushes a chapter to the top as a "weakness".
 */
export function sortByWeaknessRated<T extends ChapterStats>(rows: T[]): T[] {
  const indexed = rows.map((row, index) => ({ row, index, rated: trafficLight(row) !== 'none' }));
  const rated = indexed.filter(item => item.rated)
    .sort((a, b) => (correctRatio(a.row) as number) - (correctRatio(b.row) as number) || a.index - b.index);
  const rest = indexed.filter(item => !item.rated);
  return rated.concat(rest).map(item => item.row);
}

/** The `limit` weakest rated chapters of a paper (never chapters with too little data). */
export function weakestRated<T extends ChapterStats>(rows: T[], limit = 3): T[] {
  return sortByWeaknessRated(rows).filter(row => trafficLight(row) !== 'none').slice(0, limit);
}
